<template>
    <div class="p-4">
        <!-- Filtros -->
        <div class="card mb-3">
            <div
                class="card-header d-flex align-items-center justify-content-between py-2"
                style="cursor: pointer;"
                @click="filtrosAbiertos = !filtrosAbiertos"
            >
                <span class="fw-semibold small"><i class="bi bi-funnel me-1" />Filtros</span>
                <i :class="filtrosAbiertos ? 'bi-chevron-up' : 'bi-chevron-down'" class="bi small text-muted" />
            </div>
            <div v-show="filtrosAbiertos" class="card-body py-2">
                <div class="row g-2 align-items-end">
                    <div class="col-12 col-md-4">
                        <label class="form-label small mb-1">Buscar</label>
                        <input
                            v-model="search"
                            type="text"
                            class="form-control form-control-sm"
                            placeholder="Cliente, observaciones..."
                            @input="onSearchInput"
                        />
                    </div>
                    <div class="col-12 col-md-3">
                        <label class="form-label small mb-1">Cliente</label>
                        <ClienteSelect v-model="clienteFiltro" @update:model-value="actualizarUrl(1)" />
                    </div>
                    <div class="col-12 col-md-3">
                        <label class="form-label small mb-1">Partes</label>
                        <select v-model="todos" class="form-select form-select-sm" @change="actualizarUrl(1)">
                            <option :value="false">Mis partes</option>
                            <option :value="true">Todos</option>
                        </select>
                    </div>
                    <div class="col-auto">
                        <button class="btn btn-sm btn-secondary" @click="limpiarFiltros">Limpiar</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Tabla -->
        <div class="card">
            <div class="card-body p-0">
                <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border spinner-border-sm text-secondary" role="status">
                        <span class="visually-hidden">Cargando...</span>
                    </div>
                </div>
                <div v-else-if="error" class="alert alert-danger m-3">{{ error }}</div>
                <div v-else-if="resultado && resultado.items.length === 0" class="text-center text-muted py-5 small">
                    No se encontraron partes.
                </div>
                <template v-else-if="resultado">
                    <!-- Toggle descripción (mobile) -->
                    <div class="d-md-none border-bottom px-3 py-2 text-end">
                        <button class="btn btn-sm btn-link p-0 text-muted text-decoration-none" @click="mostrarDescripcion = !mostrarDescripcion">
                            <i class="bi me-1" :class="mostrarDescripcion ? 'bi-arrows-collapse' : 'bi-arrows-expand'" />
                            {{ mostrarDescripcion ? 'Ocultar descripción' : 'Ver descripción' }}
                        </button>
                    </div>

                    <!-- Cards (mobile) -->
                    <div class="d-md-none overflow-hidden">
                        <RouterLink
                            v-for="parte in resultado.items"
                            :key="parte.idParte"
                            :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }"
                            class="border-bottom px-3 py-2 text-decoration-none d-block text-dark"
                        >
                            <div class="d-flex justify-content-between align-items-center mb-1">
                                <span class="text-muted small">#{{ parte.idParte }} · {{ formatFecha(parte.fecha) }}</span>
                                <span class="badge" :class="parte.cerrado ? 'bg-success' : 'bg-warning text-dark'">
                                    {{ parte.cerrado ? 'Cerrado' : 'Abierto' }}
                                </span>
                            </div>
                            <div class="fw-semibold small" v-html="highlight(parte.cliente?.nombreFiscal, search)" />
                            <div v-if="parte.observaciones" class="small text-muted" :class="{ 'text-clamp-2': !mostrarDescripcion }" v-html="highlight(parte.observaciones, search)" />
                            <div v-if="parte.tramosString" class="small text-muted mt-1" v-html="highlight(parte.tramosString, search, true)" />
                        </RouterLink>
                    </div>

                    <!-- Tabla (desktop) -->
                    <div class="d-none d-md-block table-responsive">
                        <table class="table table-hover table-sm mb-0">
                            <thead class="table-light">
                                <tr>
                                    <th class="ps-3 border-0 small">#</th>
                                    <th class="border-0 small text-nowrap">Fecha</th>
                                    <th class="border-0 small">Cliente</th>
                                    <th class="d-none d-md-table-cell border-0 small">Aviso</th>
                                    <th class="d-none d-lg-table-cell border-0 small">Tipo</th>
                                    <th class="d-none d-xl-table-cell border-0 small">Observaciones</th>
                                    <th class="border-0 small">Tramos</th>
                                    <th class="border-0 small">Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="parte in resultado.items"
                                    :key="parte.idParte"
                                >
                                    <td class="ps-0 small border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-muted ps-3 py-2 d-block stretched-link">
                                            {{ parte.idParte }}
                                        </RouterLink>
                                    </td>
                                    <td class="small text-nowrap border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-dark py-2 d-block stretched-link">
                                            {{ formatFecha(parte.fecha) }}
                                        </RouterLink>
                                    </td>
                                    <td class="small border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-dark py-2 d-block stretched-link" v-html="highlight(parte.cliente?.nombreFiscal, search)" />
                                    </td>
                                    <td class="small d-none d-md-table-cell text-muted border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-muted py-2 d-block stretched-link" v-html="highlight(parte.aviso?.idAviso != null ? String(parte.aviso.idAviso) : null, search)" />
                                    </td>
                                    <td class="small d-none d-lg-table-cell text-muted border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-muted py-2 d-block stretched-link" v-html="highlight(parte.tipoParte?.descripcion, search)" />
                                    </td>
                                    <td class="small d-none d-xl-table-cell border-0 py-0 position-relative" style="max-width: 220px;">
                                        <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-dark py-2 d-block stretched-link">
                                            <div
                                                v-if="parte.observaciones"
                                                class="text-truncate"
                                                :title="parte.observaciones"
                                                v-html="highlight(parte.observaciones, search)"
                                            />
                                            <span v-else class="text-muted">—</span>
                                        </RouterLink>
                                    </td>
                                    <td class="small border-0 py-0 position-relative" style="white-space: nowrap;">
                                        <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-dark py-2 d-block stretched-link" v-html="highlight(parte.tramosString, search, true)" />
                                    </td>
                                    <td class="border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none py-2 d-block stretched-link">
                                            <span
                                                class="badge"
                                                :class="parte.cerrado ? 'bg-success' : 'bg-warning text-dark'"
                                            >
                                                {{ parte.cerrado ? 'Cerrado' : 'Abierto' }}
                                            </span>
                                        </RouterLink>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="d-flex align-items-center justify-content-between px-3 py-2 border-top">
                        <span class="small text-muted">
                            {{ resultado.totalCount }} registros &middot; Página {{ resultado.page }} de {{ resultado.totalPages }}
                        </span>
                        <div class="d-flex gap-1">
                            <button
                                class="btn btn-sm btn-outline-secondary"
                                :disabled="resultado.page <= 1"
                                @click="cambiarPagina(resultado.page - 1)"
                            ><i class="bi bi-chevron-left" /></button>
                            <button
                                class="btn btn-sm btn-outline-secondary"
                                :disabled="resultado.page >= resultado.totalPages"
                                @click="cambiarPagina(resultado.page + 1)"
                            ><i class="bi bi-chevron-right" /></button>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePageHeader } from '@/composables/usePageHeader'
import { useBreadcrumbs } from '@/composables/useBreadcrumbs'
import { misPartesApi } from '@/api/misPartesApi'
import type { Parte, PaginatedResponse } from '@/types/parte'
import type { Cliente } from '@/types/cliente'
import { highlight } from '@/utils/highlight'
import ClienteSelect from '@/components/ClienteSelect.vue'
import { clientesApi } from '@/api/clientesApi'

usePageHeader('Mis partes')
useBreadcrumbs([{ label: 'Mis partes' }])

const PAGE_SIZE = 20
const route = useRoute()
const router = useRouter()

const filtrosAbiertos = ref(window.innerWidth >= 768)
const mostrarDescripcion = ref(false)
const todos = ref(route.query.todos === '1')
const search = ref((route.query.search as string) ?? '')
const clienteFiltro = ref<Cliente | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const resultado = ref<PaginatedResponse<Parte> | null>(null)

let debounceTimer: ReturnType<typeof setTimeout> | null = null

function onSearchInput() {
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => actualizarUrl(1), 400)
}

function limpiarFiltros() {
    search.value = ''
    todos.value = false
    clienteFiltro.value = null
    actualizarUrl(1)
}

function cambiarPagina(pagina: number) {
    actualizarUrl(pagina)
}

function actualizarUrl(pagina: number) {
    router.push({
        query: {
            ...(todos.value ? { todos: '1' } : {}),
            ...(search.value ? { search: search.value } : {}),
            ...(clienteFiltro.value ? { idc: clienteFiltro.value.idc } : {}),
            ...(pagina > 1 ? { page: String(pagina) } : {}),
        },
    })
}

function formatFecha(fecha: string): string {
    return new Date(fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
}


async function cargar() {
    const pagina = Number(route.query.page) || 1
    loading.value = true
    error.value = null
    try {
        resultado.value = await misPartesApi.getAll({
            ...(todos.value ? { todos: 1 } : {}),
            search: search.value || undefined,
            idCliente: clienteFiltro.value?.idc || undefined,
            page: pagina,
            pageSize: PAGE_SIZE,
        })
    } catch {
        error.value = 'No se pudieron cargar los partes.'
    } finally {
        loading.value = false
    }
}

watch(
    () => route.query,
    async (q) => {
        todos.value = q.todos === '1'
        search.value = (q.search as string) ?? ''
        const idc = q.idc as string | undefined
        if (idc && clienteFiltro.value?.idc !== idc) {
            try { clienteFiltro.value = await clientesApi.getById(idc) } catch { clienteFiltro.value = null }
        } else if (!idc) {
            clienteFiltro.value = null
        }
        cargar()
    },
    { immediate: true },
)
</script>

<style scoped>
.text-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.row-link {
    cursor: pointer;
}
</style>
